import ConfirmAction from '@/components/confirm-action';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDateTime } from '@/lib/format';
import { app_connections_path, app_invoice_books_entry_path, billing_path } from '@/rails/routes';
import type { AppInvoice, Connection } from '@/types/platform';
import { Link, router } from '@inertiajs/react';

interface BooksCardProps {
  appId: string;
  invoice: AppInvoice;
  booksConnection: Connection | null;
  // Whether the account's plan includes QuickBooks.
  quickbooksIncluded: boolean;
}

// Sending an invoice to QuickBooks. A person does this, one invoice at a time,
// and confirms it first. Nothing is sent to the books on its own.
const BooksCard = ({ appId, invoice, booksConnection, quickbooksIncluded }: BooksCardProps) => {
  const paymentIsNext = invoice.status === 'paid' && invoice.booksState === 'invoice_sent';
  // Only an open or paid invoice goes to the books. Once the invoice is there,
  // the only thing left to send is its payment.
  const sendable = invoice.status === 'open' || invoice.status === 'paid';
  const canSend = sendable && (invoice.booksState === 'not_sent' || paymentIsNext);
  const closed = invoice.status === 'void' || invoice.status === 'uncollectible';
  const label = paymentIsNext ? 'Send payment to QuickBooks' : 'Send to QuickBooks';
  // The confirmation says exactly what will be written to the owner's books.
  const consequence = paymentIsNext
    ? 'This records the payment in your QuickBooks company.'
    : invoice.status === 'paid'
      ? 'This creates the invoice in your QuickBooks company. It is already paid, so the payment is recorded there too.'
      : 'This creates the invoice in your QuickBooks company.';

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>QuickBooks</h2>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {invoice.booksState === 'not_sent' ? null : (
          <p>
            {invoice.booksState === 'payment_sent'
              ? 'The invoice and its payment are in QuickBooks.'
              : 'The invoice is in QuickBooks.'}
            {invoice.quickbooksSyncedAt
              ? ` Last sent ${formatDateTime(invoice.quickbooksSyncedAt)}.`
              : ''}
          </p>
        )}

        {quickbooksIncluded ? (
          <>
            {invoice.status === 'draft' ? (
              <p className="text-muted-foreground">
                Send the invoice to the customer first. After that it can go to QuickBooks.
              </p>
            ) : null}

            {invoice.status === 'open' && invoice.booksState === 'invoice_sent' ? (
              <p className="text-muted-foreground">
                Once it is paid, you can send the payment too.
              </p>
            ) : null}

            {closed && invoice.booksState === 'not_sent' ? (
              <p className="text-muted-foreground">
                Only an open or paid invoice can be sent to QuickBooks.
              </p>
            ) : null}

            {canSend ? (
              booksConnection ? (
                <ConfirmAction
                  title={`${label}?`}
                  description={consequence}
                  confirmLabel={label}
                  onConfirm={() =>
                    router.post(
                      app_invoice_books_entry_path(appId, invoice.id),
                      {},
                      { preserveScroll: true }
                    )
                  }
                >
                  <Button type="button" variant="outline">
                    {label}
                  </Button>
                </ConfirmAction>
              ) : (
                <>
                  <Button type="button" variant="outline" disabled>
                    {label}
                  </Button>
                  <p className="text-muted-foreground">
                    Connect QuickBooks to send invoices to your books.{' '}
                    <Link
                      href={app_connections_path(appId)}
                      className="text-foreground font-medium underline"
                    >
                      Connections
                    </Link>
                  </p>
                </>
              )
            ) : null}
          </>
        ) : (
          <p className="text-muted-foreground">
            QuickBooks is part of Build or Studio.{' '}
            <Link href={billing_path()} className="text-foreground font-medium underline">
              See plans
            </Link>
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default BooksCard;
